/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  MenuItem,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

const formatCurrency = (value, lang) => {
  if (value == null) return '-';
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

export default function Expenses() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const { lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expenses, setExpenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    name: '',
    amount: '',
    category: '',
    note: '',
    recurring: false,
    recurring_day_offset: 3,
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amount: '', category: '', note: '' });
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [monthFilterEnabled, setMonthFilterEnabled] = useState(true);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (monthFilterEnabled && selectedMonth) params.month = selectedMonth;
      if (selectedUserId !== 'all') params.user_id = selectedUserId;
      const res = await API.get('/expenses', { params });
      setExpenses(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des depenses', 'فشل تحميل المصاريف'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await API.get('/expenses/categories');
      setCategories(res.data || []);
      if (res.data.length > 0) {
        setForm((prev) => ({ ...prev, category: prev.category || res.data[0] }));
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des categories', 'فشل تحميل الفئات'), 'error');
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await API.get('/expenses/templates');
      setTemplates(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des modeles', 'فشل تحميل القوالب'), 'error');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await API.get('/users');
      setUsers(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des utilisateurs', 'فشل تحميل المستخدمين'), 'error');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchTemplates();
    fetchUsers();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchExpenses();
  }, [selectedMonth, selectedUserId, monthFilterEnabled]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/expenses', {
        name: form.name,
        amount: Number(form.amount),
        category: form.category,
        note: form.note,
        recurring: form.recurring,
        recurring_day_offset: Number(form.recurring_day_offset),
      });
      setForm((prev) => ({ ...prev, name: '', amount: '', note: '', recurring: false }));
      showSnackbar(tr('Depense enregistree', 'تم تسجيل المصروف'), 'success');
      fetchExpenses();
      fetchTemplates();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la creation', 'فشل الإنشاء'), 'error');
    }
  };

  const startEdit = (exp) => {
    setEditingId(exp.id);
    setEditForm({
      name: exp.name,
      amount: exp.amount,
      category: exp.category,
      note: exp.note || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', amount: '', category: '', note: '' });
  };

  const handleEditChange = (e) =>
    setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const handleEditSubmit = async (expId) => {
    try {
      await API.put(`/expenses/${expId}`, {
        name: editForm.name,
        amount: Number(editForm.amount),
        category: editForm.category,
        note: editForm.note,
      });
      showSnackbar(tr('Depense mise a jour', 'تم تحديث المصروف'), 'success');
      setEditingId(null);
      fetchExpenses();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la mise a jour', 'فشل التحديث'), 'error');
    }
  };

  const handlePay = async (expId) => {
    try {
      await API.post(`/expenses/${expId}/pay`);
      showSnackbar(tr('Depense payee', 'تم دفع المصروف'), 'success');
      fetchExpenses();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du paiement', 'فشل الدفع'), 'error');
    }
  };

  const handleTemplateUpdate = async (template) => {
    try {
      await API.put(`/expenses/templates/${template.id}`, {
        amount_default: Number(template.amount_default),
        day_offset: Number(template.day_offset),
        active: template.active !== 0,
      });
      showSnackbar(tr('Modele mis a jour', 'تم تحديث القالب'), 'success');
      fetchTemplates();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la mise a jour du modele', 'فشل تحديث القالب'), 'error');
    }
  };

  const handleTemplateDelete = async (templateId) => {
    if (!window.confirm(tr('Supprimer ce modele ?', 'حذف هذا القالب؟'))) return;
    try {
      await API.delete(`/expenses/templates/${templateId}`);
      showSnackbar(tr('Modele supprime', 'تم حذف القالب'), 'success');
      setTemplates((prev) => prev.filter((tpl) => tpl.id !== templateId));
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la suppression du modele', 'فشل حذف القالب'), 'error');
    }
  };

  const filteredExpenses = expenses;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          {tr('Depenses', 'المصاريف')}
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 500 }}>
          {tr('Suivi des depenses cash par categorie.', 'متابعة المصاريف النقدية حسب الفئة.')}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={4}
          justifyContent="center"
        >
          <Chip
            label={`${tr('Depenses', 'المصاريف')}: ${filteredExpenses.length}`}
            sx={{ bgcolor: '#ef4444', color: 'var(--text-primary)' }}
          />
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={3}
          justifyContent="center"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={monthFilterEnabled}
                onChange={(e) => setMonthFilterEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={tr('Filtrer par mois', 'تصفية حسب الشهر')}
            sx={{ color: 'var(--text-primary)' }}
          />
          <TextField
            type="month"
            label={tr('Mois', 'الشهر')}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: { xs: '100%', sm: 180 } }}
            disabled={!monthFilterEnabled}
          />
          <TextField
            select
            label={tr('Employe', 'الموظف')}
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: { xs: '100%', sm: 200 } }}
          >
            <MenuItem value="all">{tr('Tous', 'الكل')}</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.username}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      <Paper
        sx={{
          p: 4,
          mb: 6,
          borderRadius: 4,
          background:
            'linear-gradient(135deg, rgba(239,68,68,0.16), rgba(5,150,105,0.12))',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowForm((v) => !v)}
            disabled={user?.role !== 'ADMIN'}
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              px: 3,
              borderRadius: 3,
              fontWeight: 600,
              background: 'linear-gradient(90deg, #ef4444, #f97316)',
            }}
          >
            {showForm ? tr('Fermer', 'إغلاق') : tr('Ajouter', 'إضافة')}
          </Button>
        </Stack>

        {showForm && (
          <Box component="form" onSubmit={handleSubmit}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                name="name"
                label={tr('Nom', 'الاسم')}
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                name="amount"
                label={tr('Montant (EUR)', 'المبلغ (EUR)')}
                type="number"
                value={form.amount}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                select
                name="category"
                label={tr('Categorie', 'الفئة')}
                value={form.category}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="note"
                label={tr('Note', 'ملاحظة')}
                value={form.note}
                onChange={handleChange}
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                select
                name="recurring"
                label={tr('Recurrent', 'متكرر')}
                value={form.recurring ? 'yes' : 'no'}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, recurring: e.target.value === 'yes' }))
                }
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              >
                <MenuItem value="no">{tr('Non', 'لا')}</MenuItem>
                <MenuItem value="yes">{tr('Oui', 'نعم')}</MenuItem>
              </TextField>
              {form.recurring && (
                <TextField
                  name="recurring_day_offset"
                  label={tr('Jours avant fin du mois', 'أيام قبل نهاية الشهر')}
                  type="number"
                  value={form.recurring_day_offset}
                  onChange={handleChange}
                  fullWidth
                  disabled={user?.role !== 'ADMIN'}
                  sx={{
                    backgroundColor: 'var(--input-bg)',
                    borderRadius: 2,
                  }}
                />
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  px: 4,
                  borderRadius: 3,
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #ef4444, #f97316)',
                }}
              >
                {tr('Enregistrer', 'حفظ')}
              </Button>
            </Stack>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ color: 'var(--text-primary)' }} />
            </Stack>
          ) : isMobile ? (
            <Stack spacing={2}>
                {filteredExpenses.map((exp) => (
                  <Paper
                  key={exp.id}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    background:
                      'var(--surface-1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                    <Stack spacing={0.6}>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {exp.name} ({exp.category})
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {formatCurrency(exp.amount, lang)}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        {tr('Employe', 'الموظف')}: {exp.username || '-'}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {exp.note || '-'}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-dim)', fontSize: 11 }}>
                        {exp.status} Â· {exp.created_at}
                      </Typography>
                      {exp.status === 'OPEN' && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handlePay(exp.id)}
                          disabled={user?.role !== 'ADMIN'}
                        >
                          {tr('Payer', 'دفع')}
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
          ) : (
            <Paper
              sx={{
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-subtle)',
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ maxHeight: { xs: 420, md: 560 } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {[tr('Nom', 'الاسم'), tr('Employe', 'الموظف'), tr('Categorie', 'الفئة'), tr('Montant', 'المبلغ'), tr('Statut', 'الحالة'), tr('Note', 'ملاحظة'), tr('Date', 'التاريخ'), tr('Action', 'إجراء')].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                            borderBottom: '1px solid var(--border-subtle)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredExpenses.map((exp) => (
                      <TableRow
                        key={exp.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'var(--surface-3)',
                          },
                        }}
                      >
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="name"
                              size="small"
                              value={editForm.name}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                            />
                          ) : (
                            exp.name
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {exp.username || '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="category"
                              select
                              size="small"
                              value={editForm.category}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                            >
                              {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            exp.category
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="amount"
                              size="small"
                              type="number"
                              value={editForm.amount}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                            />
                          ) : (
                            formatCurrency(exp.amount, lang)
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {exp.status}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="note"
                              size="small"
                              value={editForm.note}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                            />
                          ) : (
                            exp.note || '-'
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {exp.created_at}
                        </TableCell>
                        <TableCell>
                          {exp.status === 'OPEN' ? (
                            editingId === exp.id ? (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleEditSubmit(exp.id)}
                                  disabled={user?.role !== 'ADMIN'}
                                >
                                  {tr('Enregistrer', 'حفظ')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={cancelEdit}
                                >
                                  {tr('Annuler', 'إلغاء')}
                                </Button>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => startEdit(exp)}
                                >
                                  {tr('Modifier', 'تعديل')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handlePay(exp.id)}
                                  disabled={user?.role !== 'ADMIN'}
                                >
                                  {tr('Payer', 'دفع')}
                                </Button>
                              </Stack>
                            )
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 6,
          borderRadius: 4,
          background:
            'linear-gradient(135deg, rgba(22,163,74,0.14), rgba(16,185,129,0.1))',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
          {tr('Depenses recurrentes (modeles)', 'المصاريف المتكررة (القوالب)')}
        </Typography>
        {templates.length === 0 ? (
          <Typography sx={{ color: 'var(--text-muted)' }}>
            {tr('Aucun modele actif.', 'لا يوجد قالب نشط.')}
          </Typography>
        ) : isMobile ? (
          <Stack spacing={2}>
            {templates.map((tpl, index) => (
              <Paper
                key={tpl.id}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <Stack spacing={1.2}>
                  <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                    {tpl.name}
                  </Typography>
                  <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {tpl.category}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    label={tr('Montant', 'المبلغ')}
                    value={tpl.amount_default}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTemplates((prev) =>
                        prev.map((row, i) =>
                          i === index ? { ...row, amount_default: value } : row
                        )
                      );
                    }}
                    sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label={tr('Jours avant fin', 'أيام قبل النهاية')}
                    value={tpl.day_offset}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTemplates((prev) =>
                        prev.map((row, i) =>
                          i === index ? { ...row, day_offset: value } : row
                        )
                      );
                    }}
                    sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                  />
                  <TextField
                    select
                    size="small"
                    label={tr('Actif', 'نشط')}
                    value={tpl.active ? 'yes' : 'no'}
                    onChange={(e) => {
                      const value = e.target.value === 'yes';
                      setTemplates((prev) =>
                        prev.map((row, i) =>
                          i === index ? { ...row, active: value } : row
                        )
                      );
                    }}
                    sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                  >
                    <MenuItem value="yes">{tr('Oui', 'نعم')}</MenuItem>
                    <MenuItem value="no">{tr('Non', 'لا')}</MenuItem>
                  </TextField>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleTemplateUpdate(tpl)}
                      disabled={user?.role !== 'ADMIN'}
                    >
                      {tr('Enregistrer', 'حفظ')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleTemplateDelete(tpl.id)}
                      disabled={user?.role !== 'ADMIN'}
                    >
                      {tr('Supprimer', 'حذف')}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[tr('Nom', 'الاسم'), tr('Categorie', 'الفئة'), tr('Montant', 'المبلغ'), tr('Jours avant fin', 'أيام قبل النهاية'), tr('Actif', 'نشط'), tr('Enregistrer', 'حفظ'), tr('Supprimer', 'حذف')].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        color: 'var(--text-primary)',
                        fontWeight: 700,
                        backgroundColor: 'var(--table-header-bg)',
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((tpl, index) => (
                  <TableRow key={tpl.id}>
                    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>{tpl.name}</TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>{tpl.category}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={tpl.amount_default}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTemplates((prev) =>
                            prev.map((row, i) =>
                              i === index ? { ...row, amount_default: value } : row
                            )
                          );
                        }}
                        sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={tpl.day_offset}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTemplates((prev) =>
                            prev.map((row, i) =>
                              i === index ? { ...row, day_offset: value } : row
                            )
                          );
                        }}
                        sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={tpl.active ? 'yes' : 'no'}
                        onChange={(e) => {
                          const value = e.target.value === 'yes';
                          setTemplates((prev) =>
                            prev.map((row, i) =>
                              i === index ? { ...row, active: value } : row
                            )
                          );
                        }}
                        sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 110 }}
                      >
                        <MenuItem value="yes">{tr('Oui', 'نعم')}</MenuItem>
                        <MenuItem value="no">{tr('Non', 'لا')}</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleTemplateUpdate(tpl)}
                        disabled={user?.role !== 'ADMIN'}
                      >
                        {tr('Enregistrer', 'حفظ')}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleTemplateDelete(tpl.id)}
                        disabled={user?.role !== 'ADMIN'}
                      >
                        {tr('Supprimer', 'حذف')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}








